import React, { useEffect, useRef, useState } from 'react';
import { Route, Routes, BrowserRouter, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import Home from "./views/Home";
import Layout from "./layouts/Layout";
import SelectGame from "./views/SelectGame";
import DailyCheckin from "./views/DailyCheckin";
import DailyTasks from "./views/DailyTasks";
import DailyGamingTasks from "./views/DailyGamingTasks";
import EslOrignals from "./views/EslOrignals";
import Highlights from "./views/Highlights";
import WeeklyTournaments from "./views/WeeklyTournaments";
import Rewards from "./views/Rewards";
import WeeklyTournamentJoin from "./views/WeeklyTournamentJoin";
import SquadTournamentJoin from "./views/SquadTournamentJoin";
import EslTournamentJoin from "./views/EslTournamentJoin";
import GameChallenges from "./views/GameChallenges";
import { authenticateUser, isAuthenticated, getActiveDayData, updateActiveDay } from './api/auth';
import { getUserProfile } from './api/profile';
import Leaderboard from "./views/Leaderboard";
import CompletedTasks from "./views/CompletedTasks";
import Notifications from "./views/Notifications";
import GameHighlights from "./views/GameHighlights";
import Profile from "./views/Profile";
import EditProfile from "./views/EditProfile";
import FAQs from "./views/FAQs";
import Unsubscribe from "./views/Unsubscribe";
import LearnWin from './views/LearnWin';
import GoogleAnalyticsConditional from './components/GoogleAnalyticsConditional';
import RedeemForm from './views/RedeemForm';
import Thankyou from './views/Thankyou';
import PrivacyPolicy from './views/Terms';
import AimTrain from './views/Games/AimTrain';
import CannonClash2D from './views/Games/CannonClash2D';
import PuzzleRotate from './views/Games/PuzzleRotate';
import TournamentDetails from './views/TournamentDetails';
import SpaceFootball from './views/Games/SpaceFootball';
import EonDash from './views/Games/EonDash';
import AimTrainStg from './views/Games/AimTrain_stg';
import { jwtDecode } from 'jwt-decode';
import PremiumUpgrade from './views/PremiumUpgrade';
import Permissions from './views/Permissions';
import RoxLanding from './views/RoxToESL/RoxLanding';
import { normalizeMsisdn } from './utils/msisdn';

const NO_LAYOUT_ROUTES = ['/welcome/rox'];

const fetchActiveDay = async (setShowGameSelector, setShowDailyCheckin, suppressPopups) => {
  try {
    if (isAuthenticated()) {

      const activeDayData = await getActiveDayData();

      if (activeDayData) {
        sessionStorage.setItem("activeDayData", JSON.stringify(activeDayData));
        sessionStorage.setItem('activeDay', activeDayData.active_streak);
        sessionStorage.setItem('gameId', activeDayData.gameId);

        if (!suppressPopups) {
          const gameSelectorShown = sessionStorage.getItem('gameSelectorShown');
          const dailyCheckinShown = sessionStorage.getItem('dailyCheckinShown');
          {
            if (!dailyCheckinShown) {
              setShowDailyCheckin(true);
              sessionStorage.setItem('dailyCheckinShown', 'true');
            }
          }

          if (!activeDayData.gameId) {
            if (!gameSelectorShown) {
              sessionStorage.setItem('activeDay', JSON.stringify(activeDayData.active_day));
              setShowGameSelector(true);
              sessionStorage.setItem('gameSelectorShown', 'true');
            }
          }
        }
      }
    }
  } catch (error) {
  }
};

const MsisdnCheck = ({ setShowGameSelector, setShowDailyCheckin }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate()
  const location = useLocation()
  const hasCheckedRef = useRef(false);
  useEffect(() => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    const checkAuth = async () => {
      const paramsToStrip = ['a', 'refferer', 'referer', 'r'].filter((key) => searchParams.has(key));
      const isRoxWelcome = NO_LAYOUT_ROUTES.includes(location.pathname);

      let msisdn = sessionStorage.getItem('msisdn');
      let refferer = searchParams.get('refferer') || sessionStorage.getItem('refferer') || searchParams.get('referer');
      let authResult;
      if (refferer && (refferer === "gamenow")) {
        sessionStorage.setItem('gamesNowRef', true);
      }
      if (!msisdn) {
        msisdn = normalizeMsisdn(searchParams.get('a'));
        if (msisdn) {
          if (refferer === "gamenow") {
            authResult = await authenticateUser(msisdn, refferer);
          } else {
            authResult = await authenticateUser(msisdn, null);
          }
          if (!authResult.success) {
            redirectToLogin();
          } else {
            window.dispatchEvent(new CustomEvent('authComplete'));
            await fetchActiveDay(setShowGameSelector, setShowDailyCheckin, isRoxWelcome);
          }
        } else {
          redirectToLogin();
        }
      } else if (!isAuthenticated()) {
        const authResult = await authenticateUser(msisdn, refferer);
        if (!authResult.success) {
          redirectToLogin();
        } else {
          window.dispatchEvent(new CustomEvent('authComplete'));
          await fetchActiveDay(setShowGameSelector, setShowDailyCheckin, isRoxWelcome);
        }
      } else {
        await fetchActiveDay(setShowGameSelector, setShowDailyCheckin, isRoxWelcome);
      }

      const rew = searchParams.get('r');
      if (rew && rew == 1) {
        setShowDailyCheckin(false)
        setShowGameSelector(false)
        navigate('/rewards', { replace: true })
      } else if (paramsToStrip.length) {
        const cleanedParams = new URLSearchParams(window.location.search);
        paramsToStrip.forEach((key) => cleanedParams.delete(key));
        navigate({ pathname: location.pathname, search: cleanedParams.toString() }, { replace: true });
      }
    };

    const redirectToLogin = () => {
      const loginUrl = process.env.REACT_APP_LOGIN_URL;
      if (window.location.pathname === '/tasks' || window.location.pathname === '/join-tournament' || window.location.pathname === '/join-squad-tournament' || window.location.pathname === '/redeem' || window.location.pathname == "/profile") {
        if (sessionStorage.getItem("gamesNowRef") != 'true')
          window.location.href = loginUrl;
      }
    };

    checkAuth();
  }, [searchParams, setShowGameSelector, setShowDailyCheckin]);

  return null;
};

const RoutePopups = ({ showGameSelector, showDailyCheckin, handleCloseGameSelector, handleCloseDailyCheckin }) => {
  const location = useLocation();
  if (NO_LAYOUT_ROUTES.includes(location.pathname)) return null;

  return (
    <>
      {showGameSelector && <SelectGame isOpen={showGameSelector} onClose={handleCloseGameSelector} />}
      {showDailyCheckin && <DailyCheckin isOpen={showDailyCheckin} onClose={handleCloseDailyCheckin} />}
    </>
  );
};

const getIsJcUser = () => {
  const accessToken = sessionStorage.getItem('access_token');
  if (!accessToken) return false;

  try {
    const decoded = jwtDecode(accessToken);
    return String(decoded?.is_premium) === '1';
  } catch {
    return false;
  }
};

const AppRoutes = ({ isJcUser, profileData, setShowGameSelector, setShowDailyCheckin }) => {
  const location = useLocation();
  const shouldShowLayout = !NO_LAYOUT_ROUTES.includes(location.pathname);
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const wasRoxWelcome = NO_LAYOUT_ROUTES.includes(prevPathRef.current);
    prevPathRef.current = location.pathname;
    if (wasRoxWelcome && !NO_LAYOUT_ROUTES.includes(location.pathname)) {
      fetchActiveDay(setShowGameSelector, setShowDailyCheckin, false);
    }
  }, [location.pathname, setShowGameSelector, setShowDailyCheckin]);

  const routes = (
    <Routes>
      <Route path="/" element={<Home isJcUser={isJcUser} />} />
      <Route path="/tasks" element={<DailyTasks />} />
      <Route path="/daily-gaming-tasks" element={<DailyGamingTasks isJcUser={isJcUser} />} />
      <Route path="/esl-orignals" element={<EslOrignals isJcUser={isJcUser} />} />
      <Route path="/game-challenges" element={<GameChallenges />} />
      <Route path="/highlights" element={<Highlights />} />
      <Route path='/game-highlights' element={<GameHighlights />} />
      <Route path="/tournaments" element={<WeeklyTournaments isJcUser={isJcUser} />} />
      <Route path="/join-tournament" element={<WeeklyTournamentJoin isJcUser={isJcUser} />} />
      <Route path="/join-squad-tournament" element={<SquadTournamentJoin isJcUser={isJcUser} />} />
      <Route path="/join-esl-tournament" element={<EslTournamentJoin />} />
      <Route path="/rewards" element={<Rewards isJcUser={isJcUser} />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/completed" element={<CompletedTasks />} />
      <Route path="/notifications" element={<Notifications />} />
      <Route path="/profile" element={<Profile isJcUser={isJcUser} />} />
      <Route path="/edit-profile" element={<EditProfile />} />
      <Route path="/faqs" element={<FAQs />} />
      <Route path="/permissions" element={<Permissions />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/learn-win" element={<LearnWin />} />
      <Route path='/redeem' element={<RedeemForm />} />
      <Route path='/thankyou' element={<Thankyou />} />
      <Route path='/terms-service' element={<PrivacyPolicy />} />
      <Route path='/aim-train' element={<AimTrain />} />
      <Route path='/premium-plan' element={<PremiumUpgrade />} />
      {/* <Route path='/aim-train-stg' element={<AimTrainStg />} /> */}
      <Route path='cannon-clash' element={<CannonClash2D />} />
      <Route path='/puzzle-mania' element={<PuzzleRotate />} />
      <Route path='/space-football' element={<SpaceFootball />} />
      {/* <Route path='/eon-dash' element={<EonDash />} /> */}
      <Route path='/tournament/:tournamentId' element={<TournamentDetails />} />
      <Route path='/welcome/rox' element={<RoxLanding />} />
    </Routes>
  );

  return shouldShowLayout ? (
    <Layout profileData={profileData}>{routes}</Layout>
  ) : (
    routes
  );
};

function App() {
  const [showGameSelector, setShowGameSelector] = useState(false);
  const [showDailyCheckin, setShowDailyCheckin] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [isJcUser, setIsJcUser] = useState(getIsJcUser);

  useEffect(() => {
    const fetchProfile = async () => {
      if (isAuthenticated()) {
        try {
          const response = await getUserProfile();
          if (response.success) {
            setProfileData(response);
            sessionStorage.setItem('profileData', JSON.stringify(response));
          }
        } catch (error) {
        }
      }
    };

    fetchProfile();
    window.addEventListener('authComplete', fetchProfile);

    return () => window.removeEventListener('authComplete', fetchProfile);
  }, []);

  // Re-read the premium flag when the token actually changes, instead of polling.
  useEffect(() => {
    const syncJcUser = () => {
      const value = getIsJcUser();
      setIsJcUser(prev => (prev === value ? prev : value));
    };

    window.addEventListener('authComplete', syncJcUser);
    window.addEventListener('tokenChanged', syncJcUser);
    window.addEventListener('storage', syncJcUser);

    return () => {
      window.removeEventListener('authComplete', syncJcUser);
      window.removeEventListener('tokenChanged', syncJcUser);
      window.removeEventListener('storage', syncJcUser);
    };
  }, []);

  const handleCloseGameSelector = () => {
    setShowGameSelector(false);
  };

  const handleCloseDailyCheckin = () => {
    const activeDayData = JSON.parse(sessionStorage.getItem('activeDayData'));
    setShowDailyCheckin(false);
    if (!activeDayData.gameId) {
      setShowGameSelector(true);
    }
  };

  return (
    <div>

      <BrowserRouter>
        <MsisdnCheck
          setShowGameSelector={setShowGameSelector}
          setShowDailyCheckin={setShowDailyCheckin}
        />
        {/* <GoogleAnalyticsConditional
          trackingId="G-ZLE6YPN01C"  //  JAZZ ESL ID for the login page
        /> */}
        <AppRoutes
          isJcUser={isJcUser}
          profileData={profileData}
          setShowGameSelector={setShowGameSelector}
          setShowDailyCheckin={setShowDailyCheckin}
        />
        <RoutePopups
          showGameSelector={showGameSelector}
          showDailyCheckin={showDailyCheckin}
          handleCloseGameSelector={handleCloseGameSelector}
          handleCloseDailyCheckin={handleCloseDailyCheckin}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;